import React, { useEffect, useState, memo, useMemo } from 'react'
import { Card, Col, Nav, Placeholder } from 'react-bootstrap'
import ReactApexChart from 'react-apexcharts'
import { getPeriodInventoryPerformance } from '../../../services/micro'
import { AgSplittedTable } from '../../../components/AgSplittedTable'
import { InventoryTypeRenderer } from '../../inventory/components/InventoryTypeRenderer'
import { useCookiesContext } from '../../../contexts/CookiesContext'
const customColumns = [
    {
        field: 'type',
        cellRenderer: InventoryTypeRenderer
    }
]

const colWidths = {
    ID: 100,
    name: 150,
    website: 90,
    type: 150,
    categories: 150,
    views: 150,
    bids: 150,
    freeClicks: 150,
    paidClicks: 150,
    averageCPC: 150,
    topCPC: 150,
    revenue: 150
}

const htrColWidths = {
    name: 150,
    type: 50,
    categories: 175,
    views: 75,
    bids: 24,
    freeClicks: 75,
    paidClicks: 75,
    averageCPC: 75,
    topCPC: 75,
    revenue: 75
}

const getOptionMetrics = colors => ({
    grid: {
        show: false
    },
    chart: {
        type: 'area',
        toolbar: { show: false },
        stacked: false,
        zoom: { enabled: false }
    },
    colors: [colors.chartSecondary, colors.chartPrimary],
    stroke: { curve: 'smooth', width: [2, 0.25] },
    yaxis: [
        { seriesName: 'Clicks', opposite: true, labels: { show: false } },
        { seriesName: 'Views', opposite: false, labels: { show: false } }
    ],
    fill: { type: 'gradient', gradient: { opacityFrom: 0.5, opacityTo: 0 } },
    dataLabels: { enabled: false },
    legend: { show: false },
    xaxis: {
        type: 'datetime',
        tickAmount: 6,
        labels: {
            style: { colors: '#6e7985', fontSize: '11px' },
            formatter: val => {
                const date = new Date(val)
                return `${date.getDate()} ${date.toLocaleString('en-US', { month: 'short' })}`
            }
        }
    }
})

const openInventoryInsights = id => {
    window.open(`/pub/dashboard/inventory/insights?id=${id}`, '_blank')
}

const onRowClick = event => {
    openInventoryInsights(event.data.ID)
}

const getContextMenuItems = params => [
    {
        name: 'Open ' + params.node.data.name + ' Insights',
        action: () => openInventoryInsights(params.node.data.ID)
    },
    'separator',
    'copy'
]

const getStyles = variable => getComputedStyle(document.documentElement).getPropertyValue(variable).trim()

function InventoryPerformance({ currentPeriod, skin }) {
    const { getPublisherId } = useCookiesContext();
    const publisherId = getPublisherId();
    const isHTR = +publisherId === 67;
    const [response, setResponse] = useState(null)
    const [reset, setReset] = useState(0)

    const colors = useMemo(
        () => ({
            chartPrimary: getStyles('--primary'),
            chartSecondary: getStyles('--chart-secondary'),
            chartTertiary: getStyles('--chart-tertiary')
        }),
        []
    )

    const onPerformanceReset = () => {
        setReset(prev => prev + 1)
    }

    const optionMetrics = getOptionMetrics(colors)

    useEffect(() => {
        if (!currentPeriod || !currentPeriod[0] || !currentPeriod[1]) return

        getPeriodInventoryPerformance(currentPeriod).then(res => {
            if (res) {
                setResponse(res)
            }
        })
    }, [currentPeriod])

    if (response && response?.data) {
        optionMetrics.yaxis[0].max = response.data.maxClicks
        optionMetrics.yaxis[1].max = response.data.maxViews

        let currentColumns = []
        let periodColumns = []
        let classifiedColumns = {}
        if (isHTR) {
        //in case of HTR, the columns are: ID - Name - Views — bids — paid clicks — top cpc — revenue — free clicks — avg cpc — type —  categories
        const nameColumn = response.data.columns.slice(1, 2)
         periodColumns = response.data.columns.slice(2, 9)
         currentColumns = response.data.columns.slice(9)
         classifiedColumns = { nameColumn, periodColumns, currentColumns }
        } else {
            currentColumns = response.data.columns.slice(0, 5);
            periodColumns = response.data.columns.slice(5);
            classifiedColumns = { currentColumns, periodColumns };
        }

        return (
            <Col xs="12">
                <Card className="card-one">
                    <Card.Header>
                        <Card.Title as="h6">Inventory Performance</Card.Title>
                        <Nav className="nav-icon nav-icon-sm ms-auto">
                            <Nav.Link href="#" onClick={onPerformanceReset}>
                                <i className="ri-refresh-line"></i>
                            </Nav.Link>
                        </Nav>
                    </Card.Header>
                    <Card.Body className="p-3 p-xl-3">
                        <ReactApexChart
                            key={reset}
                            series={response.data.periods}
                            options={optionMetrics}
                            type="area"
                            height={250}
                            className="apex-chart-two chart-container"
                        />
                        <AgSplittedTable
                            classifiedColumns={classifiedColumns}
                            getData={getPeriodInventoryPerformance}
                            currentPeriod={currentPeriod}
                            getContextMenuItems={getContextMenuItems}
                            skin={skin}
                            colWidths={isHTR ? htrColWidths : colWidths}
                            customColumns={customColumns}
                            onRowClick={onRowClick}
                        />
                    </Card.Body>
                </Card>
            </Col>
        )
    } else {
        return (
            <Placeholder as={Card.Title} animation="glow" style={{ color: 'var(--bs-body-color)' }}>
                <Placeholder style={{ height: 400, width: '100%' }} />
            </Placeholder>
        )
    }
}

export default memo(InventoryPerformance)
